import _findIndex from 'lodash/findIndex';

const sptlightMapping = {
  en_US: 'help',
  es_XC: 'Ayuda',
  zh_XC: '說明',
  fr_XC: 'Aide',
};

const communityChannel = 'COMMUNITY';

const channels = {
  FAQ: 'faq',
  MTS: 'mts',
  BRC: 'brc',
  DEVELOPER: 'developer',
  PRODUCT: 'product',
};

export function filterFaqID(element) {
  return element.type === 'FAQ';
}

export function filterContactChannel(element) {
  return (
    element.type === 'email' ||
    element.type === 'callus' ||
    element.type === 'community' ||
    element.type === 'seller' ||
    element.type === 'chat'
  );
}

export function filterWebformDetail(element) {
  return !filterFaqID(element) && !filterContactChannel(element);
}

export function articleDataParser(body) {
  const formElements = body.web_form_template;
  const faqid = formElements.form_elements.filter(filterFaqID);
  const contactChannel = formElements.form_elements.filter(filterContactChannel);
  const webformDetail = formElements.form_elements.filter(filterWebformDetail);

  const formObj = formElements;
  formObj.form_elements = {};
  formObj.topicRefId = body.ref_id;
  formObj.form_elements.faqid = faqid.length > 0 ? faqid : undefined;
  formObj.form_elements.contact_channel = contactChannel.length > 0 ? contactChannel : undefined;
  formObj.form_elements.webform_detail = webformDetail.length > 0 ? webformDetail : undefined;

  return { formInfo: formObj };
}

export function parseAutoComplete(response) {
  const parsedResponse = [];
  if (Array.isArray(response)) {
    response.forEach((article, index) => {
      parsedResponse.push({
        title: article.articleTitle,
        docid: article.articleId,
        rank: index + 1,
      });
    });
  }
  return parsedResponse;
}

/**
 * Parse OKM direct links response
 * @param response
 * @returns {[]} - Parsed spotlight results
 */
function parseSpotlightOKMResults(response) {
  const parsedResponse = [];
  const segmentTypes = [];
  let indexValue;
  if (response && Array.isArray(response.directlinks) && response.directlinks.length) {
    response.directlinks.forEach((val) => {
      indexValue = segmentTypes.indexOf(val.segment);
      if (indexValue === -1) {
        segmentTypes.push(val.segment);
        const obj = {
          segment: val.segment,
          suggestions: [
            {
              docid: val.displayname,
              title: val.displayname,
              action: { type: val.actiontype, url: val.url },
            },
          ],
        };
        parsedResponse.push(obj);
      } else {
        parsedResponse[indexValue].suggestions.push({
          docid: val.displayname,
          title: val.displayname,
          action: { type: val.actiontype, url: val.url },
        });
      }
    });
  }
  return parsedResponse;
}

export function communitySearchParser(response) {
  const parsedResponse = [];
  if (Array.isArray(response)) {
    response.forEach((message) => {
      parsedResponse.push({
        articleTitle: message.subject.$,
        link: message.view_href,
        views: message.views.count.$,
        articleId: message.id.$,
        channel: communityChannel,
      });
    });
  }
  return parsedResponse;
}

export function isNotEmptySuggestion(suggestions) {
  return (
    Array.isArray(suggestions) &&
    suggestions.some((suggest) => suggest && Array.isArray(suggest.suggestions) && suggest.suggestions.length > 0)
  );
}

export function parseKmSearchResults(response) {
  if (Array.isArray(response.results)) {
    return response.results.map((result, index) => ({
      title: result.title,
      docid: result.docid,
      rank: index + 1, // TODO:: For now i am assigning the index to rank. Replace this with API rank/priority once the service is ready.
    }));
  }
  return [];
}

// parse the auto complete search results from ECM API
export function parseEcmAutoCompleteSearchResults(response) {
  if (response && Array.isArray(response.ranked_entries) && response.ranked_entries.length) {
    return response.ranked_entries.map((result) => ({
      docid: result.document_id,
      title: result.document_title,
      rank: result.rank,
    }));
  }
  return [];
}

export function miniHelpEcmSearchSpotLightParser(response, q, locale) {
  const constResult = parseSpotlightOKMResults(response); // update this logic with DirectLinks from ECM
  const helpResult = parseEcmAutoCompleteSearchResults(response);
  if (Array.isArray(helpResult) && helpResult.length > 0) {
    return [
      ...constResult,
      {
        segment: sptlightMapping[locale],
        suggestions: helpResult,
      },
    ];
  }
  return constResult;
}

export function miniHelpKmSearchSpotLightParser(response, q, locale) {
  const constResult = parseSpotlightOKMResults(response);
  const helpResult = parseKmSearchResults(response);
  if (Array.isArray(helpResult) && helpResult.length > 0) {
    return [
      ...constResult,
      {
        segment: sptlightMapping[locale],
        suggestions: helpResult,
      },
    ];
  }
  return constResult;
}

// --------------------------- OKM Full Search Data Model -------------------------------------
function getBRCArticleUrl(article) {
  const country = article.locale.split('_')[1];
  return `https://www.paypal.com/${country}/brc/article/${article.content['url-slug']}`;
}

// Data model/parser for FAQ and MTS channels
function getFaqMtsDataModel(results, customerSaysIntent) {
  // Passing customersays response to each article
  return results.map((result) => ({
    articleId: result.docid,
    articleTitle: result.title,
    breadCrumbPath: result.breadCrumbPath,
    breadCrumbTitle: result.breadCrumbTitle,
    channel: result.channel,
    excerpt: result.excerpt,
    rank: result.rank,
    topicId: result.topicId,
    topicName: result.topicName,
    usage: result.usage,
    intents: result.tags,
    a_track: result.a_track,
    customerSaysIntent,
  }));
}

// Data model/parser for BRC channels
function getBrcDataModel(results) {
  return results.map((result) => ({
    articleId: result.ssdata.ssid,
    articleTitle: result.content.heading,
    channel: 'BRC',
    excerpt: result.content.desc,
    rank: result.rank,
    url: getBRCArticleUrl(result),
    usage: 0,
    a_track: result.a_track,
  }));
}

// Data model/parser for Developer and Product channels
function getDevProdDataModel(results, channel) {
  return results.map((result) => ({
    articleTitle: result.fields.title,
    channel: channel.toUpperCase(),
    excerpt: result.fields.p,
    rank: result.rank,
    url: result.fields.url,
    usage: 0,
    a_track: result.a_track,
  }));
}

// Data parser for OKM full search
export function parseKmFullSearchResults(searchResponse, channel, customerSaysIntent) {
  const results = searchResponse.results;

  if (Array.isArray(results) && results.length) {
    switch (channel) {
      case channels.FAQ:
      case channels.MTS:
        return getFaqMtsDataModel(results, customerSaysIntent);

      case channels.BRC:
        return getBrcDataModel(results);

      case channels.DEVELOPER:
      case channels.PRODUCT:
        return getDevProdDataModel(results, channel);

      default:
        return [];
    }
  }

  return [];
}

export function findRedirectFAQ(rawData, sourceFAQ) {
  if (Array.isArray(rawData) && sourceFAQ) {
    const index = _findIndex(rawData, { source_document_id: sourceFAQ.toUpperCase() });
    if (index >= 0) {
      return rawData[index].target_document_id;
    }
  }
  return false;
}

// --------------------------- ECM Full Search Data Model -------------------------------------
// Data model/parser for FAQ and MTS channels
function getEcmFaqMtsDataModel(results, customerSaysIntent) {
  // Passing customersays response to each article
  return results.map((result) => ({
    articleId: result.document_id,
    articleTitle: result.document_title,
    breadCrumbPath: result.breadCrumbPath,
    breadCrumbTitle: result.breadCrumbTitle,
    channel: result.channel,
    excerpt: result.excerpt,
    rank: result.rank,
    topicId: result.topicId,
    topicName: result.topicName,
    usage: result.usage,
    intents: result.fields.intent,
    a_track: result.analytic_track,
    customerSaysIntent,
  }));
}

// Data parser for ECM full search
export function parseEcmFullSearchResults(searchResponse, channel, customerSaysIntent) {
  const results = searchResponse.ranked_entries;

  if (Array.isArray(results) && results.length) {
    switch (channel) {
      case channels.FAQ:
      case channels.MTS:
        return getEcmFaqMtsDataModel(results, customerSaysIntent);

      default:
        return [];
    }
  }

  return [];
}
